import React, { useRef, useEffect } from 'react';
import { useLarguraTela } from '../hooks/useLarguraTela';

const TYPE_INFO = {
  aspnet: { label: 'ASP.NET Core', color: '#6d28d9', bg: '#f3e8ff' },
  react: { label: 'React', color: '#0369a1', bg: '#e0f2fe' },
  node: { label: 'Node.js', color: '#15803d', bg: '#dcfce7' },
};

const PALETA_CORES_PROJETO = [
  '#e11d48', // rose
  '#7c3aed', // purple
  '#0ea5e9', // sky
  '#16a34a', // green
  '#ea580c', // orange
  '#0891b2', // cyan
  '#d97706', // amber
  '#db2777', // pink
  '#4f46e5', // indigo
  '#65a30d', // lime
  '#be123c', // crimson
  '#0369a1', // blue
];

function corDoProjeto(id) {
  const str = String(id || '');
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = (hash * 31 + str.charCodeAt(i)) >>> 0;
  }
  return PALETA_CORES_PROJETO[hash % PALETA_CORES_PROJETO.length];
}

const STATUS_CONFIG = {
  idle: { label: 'Pronto', color: '#64748b', bg: '#f8fafc', border: '#cbd5e1' },
  deploying: { label: 'Fazendo deploy...', color: '#b45309', bg: '#fffbeb', border: '#fcd34d' },
  success: { label: 'Sucesso', color: '#15803d', bg: '#f0fdf4', border: '#86efac' },
  failed: { label: 'Falhou', color: '#b91c1c', bg: '#fef2f2', border: '#fecaca' },
};

const LOG_COLORS = {
  info: '#64748b',
  command: '#2563eb',
  stdout: '#334155',
  stderr: '#b45309',
  success: '#15803d',
  error: '#dc2626',
  warn: '#d97706',
};

const GIT_UPDATE_CONFIG = {
  updated: { label: 'Atualizado', color: '#15803d', bg: '#f0fdf4', border: '#86efac' },
  outdated: { label: 'Atrasado', color: '#b91c1c', bg: '#fef2f2', border: '#fecaca' },
  never_deployed: { label: 'Sem atualização', color: '#b45309', bg: '#fffbeb', border: '#fcd34d' },
  stale: { label: 'Verificação atrasada', color: '#b45309', bg: '#fffbeb', border: '#fcd34d' },
  unknown: { label: 'Não verificado', color: '#64748b', bg: '#f8fafc', border: '#cbd5e1' },
  error: { label: 'Erro', color: '#b91c1c', bg: '#fef2f2', border: '#fecaca' },
};

function obterConfigAtualizacao(gitUpdate) {
  if (gitUpdate?.error) return GIT_UPDATE_CONFIG.error;
  if (gitUpdate?.commitInfoStale) return GIT_UPDATE_CONFIG.stale;
  return GIT_UPDATE_CONFIG[gitUpdate?.status] || GIT_UPDATE_CONFIG.unknown;
}

function formatarDataHora(valor) {
  if (!valor) return '-';
  const data = new Date(valor);
  if (Number.isNaN(data.getTime())) return '-';
  return data.toLocaleString('pt-BR');
}

const S = {
  panel: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    overflow: 'hidden',
  },

  panelHeader: {
    padding: '24px 28px 18px',
    borderBottom: '1px solid #dbe4ef',
    flexShrink: 0,
  },
  topRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 6,
  },
  projectName: {
    fontSize: 22,
    fontWeight: 700,
    color: '#0f172a',
    letterSpacing: '-0.3px',
  },
  typeBadge: {
    fontSize: 11,
    padding: '3px 10px',
    borderRadius: 20,
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
    flexShrink: 0,
    marginLeft: 12,
  },
  description: {
    color: '#64748b',
    fontSize: 13,
    marginBottom: 12,
    lineHeight: 1.5,
  },
  infoChips: {
    display: 'flex',
    gap: 8,
    flexWrap: 'wrap',
  },
  chip: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 5,
    fontSize: 12,
    color: '#475569',
    background: '#f8fafc',
    border: '1px solid #cbd5e1',
    borderRadius: 6,
    padding: '3px 10px',
  },
  chipDisabled: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 5,
    fontSize: 12,
    color: '#b91c1c',
    background: '#fef2f2',
    border: '1px solid #fecaca',
    borderRadius: 6,
    padding: '3px 10px',
  },
  chipBranchBtn: {
    fontSize: 12,
    color: '#3b82f6',
    background: 'none',
    border: 'none',
    padding: 0,
    cursor: 'pointer',
    fontWeight: 500,
  },
  updatePanel: {
    marginTop: 12,
    padding: '10px 12px',
    background: '#f8fafc',
    border: '1px solid #dbe4ef',
    borderRadius: 8,
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
    gap: 10,
  },
  updateItem: {
    display: 'flex',
    flexDirection: 'column',
    gap: 3,
    minWidth: 0,
  },
  updateItemFull: {
    display: 'flex',
    flexDirection: 'column',
    gap: 3,
    minWidth: 0,
    gridColumn: '1 / -1',
  },
  updateLabel: {
    color: '#64748b',
    fontSize: 10,
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
  },
  updateValue: {
    color: '#0f172a',
    fontSize: 12,
    fontWeight: 600,
    wordBreak: 'break-word',
  },
  updateBadge: {
    alignSelf: 'flex-start',
    fontSize: 12,
    padding: '4px 9px',
    borderRadius: 6,
    fontWeight: 700,
  },
  updateError: {
    gridColumn: '1 / -1',
    color: '#b91c1c',
    fontSize: 12,
    background: '#fef2f2',
    border: '1px solid #fecaca',
    borderRadius: 6,
    padding: '8px 10px',
  },
  updateWarning: {
    gridColumn: '1 / -1',
    color: '#92400e',
    fontSize: 12,
    background: '#fffbeb',
    border: '1px solid #fcd34d',
    borderRadius: 6,
    padding: '8px 10px',
  },

  actionBar: {
    padding: '14px 28px',
    borderBottom: '1px solid #dbe4ef',
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    flexShrink: 0,
    flexWrap: 'wrap',
  },
  deployBtn: {
    padding: '10px 28px',
    background: '#3b82f6',
    color: '#fff',
    border: 'none',
    borderRadius: 8,
    fontSize: 14,
    fontWeight: 600,
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
  },
  deployBtnDeploying: {
    padding: '10px 28px',
    background: '#fffbeb',
    color: '#b45309',
    border: '1px solid #fcd34d',
    borderRadius: 8,
    fontSize: 14,
    fontWeight: 600,
    cursor: 'not-allowed',
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
  },
  editBtn: {
    padding: '10px 16px',
    background: 'transparent',
    color: '#334155',
    border: '1px solid #cbd5e1',
    borderRadius: 8,
    fontSize: 13,
    cursor: 'pointer',
  },
  historyBtn: {
    padding: '10px 16px',
    background: '#eff6ff',
    color: '#2563eb',
    border: '1px solid #bfdbfe',
    borderRadius: 8,
    fontSize: 13,
    cursor: 'pointer',
    fontWeight: 600,
  },
  statusGroup: {
    marginLeft: 'auto',
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    flexWrap: 'wrap',
    justifyContent: 'flex-end',
  },
  lastDeploy: {
    fontSize: 12,
    color: '#64748b',
  },
  statusBadge: {
    fontSize: 12,
    padding: '5px 12px',
    borderRadius: 20,
    fontWeight: 600,
  },

  logArea: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    background: '#ffffff',
  },
  logHeader: {
    padding: '10px 28px',
    background: '#f8fafc',
    borderBottom: '1px solid #dbe4ef',
    color: '#64748b',
    fontSize: 11,
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
    flexShrink: 0,
  },
  logBody: {
    flex: 1,
    overflowY: 'auto',
    padding: '14px 20px',
    background: '#ffffff',
    fontFamily: "'Consolas', 'Cascadia Code', 'Fira Code', monospace",
    fontSize: 12,
    lineHeight: 1.65,
  },
  logLine: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 8,
    marginBottom: 4,
    wordBreak: 'break-word',
  },
  logTimestamp: {
    color: '#64748b',
    fontSize: 11,
    minWidth: 62,
    userSelect: 'none',
  },
  logMessage: {
    flex: 1,
  },
  logEmpty: {
    color: '#64748b',
    fontSize: 13,
    background: '#f8fafc',
    border: '1px dashed #cbd5e1',
    borderRadius: 8,
    padding: '12px 14px',
  },
};

export default function ProjectCard({
  project,
  logs = [],
  status,
  onDeploy,
  onEditProject,
  onEditBranch,
  onOpenHistory,
  onOpenCommits,
  onAtualizarStatus,
  atualizandoStatus = false,
  bloquearAcoes = false,
  somenteLeitura = false,
}) {
  const logEndRef = useRef(null);
  const larguraTela = useLarguraTela();
  const ehMobile = larguraTela <= 900;
  const currentStatus = status || project.deployStatus || 'idle';
  const typeInfo = TYPE_INFO[project.type] || { label: project.type, color: '#64748b', bg: '#f1f5f9' };
  const corTitulo = corDoProjeto(project.id);
  const statusInfo = STATUS_CONFIG[currentStatus] || STATUS_CONFIG.idle;
  const updateInfo = obterConfigAtualizacao(project.gitUpdate);
  const isDeploying = currentStatus === 'deploying';
  const botoesBloqueados = bloquearAcoes || isDeploying;
  const deveMostrarStatusAtualizacao = Boolean(project.repoPath || project.gitUpdate);
  const gitUpdate = project.gitUpdate || null;

  useEffect(() => {
    if (logEndRef.current) {
      logEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [logs]);

  const handleDeploy = () => {
    if (botoesBloqueados || typeof onDeploy !== 'function') return;
    onDeploy(project.id);
  };

  return (
    <div style={S.panel}>
      <div style={{ ...S.panelHeader, padding: ehMobile ?'14px 12px 12px' : S.panelHeader.padding }}>
        <div style={{ ...S.topRow, flexWrap: ehMobile ?'wrap' : 'nowrap', gap: ehMobile ?8 : 0 }}>
          <span style={{ ...S.projectName, fontSize: ehMobile ?18 : S.projectName.fontSize }}>{project.name}</span>
          <span
            style={{
              ...S.typeBadge,
              color: typeInfo.color,
              background: typeInfo.bg,
              marginLeft: ehMobile ?0 : S.typeBadge.marginLeft,
            }}
          >
            {typeInfo.label}
          </span>
        </div>
        {project.description && <div style={{ ...S.description, fontSize: ehMobile ?12 : S.description.fontSize }}>{project.description}</div>}
        <div style={{ ...S.infoChips, gap: ehMobile ?6 : S.infoChips.gap }}>
          <div style={S.chip}>
            <span>Branch:</span>
            {typeof onEditBranch === 'function' ?(
              <button
                style={{
                  ...S.chipBranchBtn,
                  opacity: bloquearAcoes ?0.5 : 1,
                  cursor: bloquearAcoes ?'not-allowed' : S.chipBranchBtn.cursor,
                }}
                onClick={() => {
                  if (bloquearAcoes) return;
                  onEditBranch(project);
                }}
                disabled={bloquearAcoes}
              >
                {project.branch || 'main'}
              </button>
            ) : (
              <span>{project.branch || 'main'}</span>
            )}
          </div>
          {project.iisSiteName && <div style={S.chip}>IIS: {project.iisSiteName}</div>}
          {project.repoPath && (
            <div style={S.chip} title={project.repoPath}>
              {project.repoPath.length > 40 ?'...' + project.repoPath.slice(-38) : project.repoPath}
            </div>
          )}
          {!project.enabled && <div style={S.chipDisabled}>Desabilitado</div>}
          {somenteLeitura && <div style={S.chipDisabled}>Somente leitura</div>}
        </div>
        {deveMostrarStatusAtualizacao && (
          <div style={S.updatePanel}>
            <div style={S.updateItem}>
              <span style={S.updateLabel}>Atualização</span>
              <span
                style={{
                  ...S.updateBadge,
                  color: updateInfo.color,
                  background: updateInfo.bg,
                  border: `1px solid ${updateInfo.border}`,
                }}
              >
                {updateInfo.label}
              </span>
            </div>
            <div style={S.updateItem}>
              <span style={S.updateLabel}>Último commit</span>
              <span style={S.updateValue}>
                {formatarDataHora(gitUpdate?.lastCommitAt || gitUpdate?.remoteCommit?.timestamp)}
                {gitUpdate?.remoteCommit?.shortHash ? ` (${gitUpdate.remoteCommit.shortHash})` : ''}
              </span>
            </div>
            <div style={S.updateItem}>
              <span style={S.updateLabel}>Autor do commit</span>
              <span style={S.updateValue}>
                {gitUpdate?.remoteCommit?.authorName || '-'}
              </span>
            </div>
            <div style={S.updateItemFull}>
              <span style={S.updateLabel}>Descrição do commit</span>
              <span style={S.updateValue}>
                {gitUpdate?.remoteCommit?.description || gitUpdate?.remoteCommit?.message || '-'}
              </span>
            </div>
            <div style={S.updateItem}>
              <span style={S.updateLabel}>Commit local</span>
              <span style={S.updateValue}>
                {formatarDataHora(gitUpdate?.localCommit?.timestamp)}
                {gitUpdate?.localCommit?.shortHash ? ` (${gitUpdate.localCommit.shortHash})` : ''}
              </span>
            </div>
            <div style={S.updateItem}>
              <span style={S.updateLabel}>Última atualização</span>
              <span style={S.updateValue}>{formatarDataHora(gitUpdate?.lastUpdateAt || project.lastSuccessDeploy?.timestamp)}</span>
            </div>
            <div style={S.updateItem}>
              <span style={S.updateLabel}>Última verificação do commit</span>
              <span style={S.updateValue}>{formatarDataHora(gitUpdate?.lastCommitCheckedAt || gitUpdate?.checkedAt)}</span>
            </div>
            {gitUpdate?.commitInfoStale && !gitUpdate?.error && (
              <div style={S.updateWarning}>
                A relação entre atualização e commit pode estar desatualizada. Clique em Atualizar para verificar novamente.
              </div>
            )}
            {gitUpdate?.error && (
              <div style={S.updateError}>{gitUpdate.error}</div>
            )}
          </div>
        )}
      </div>

      <div style={{ ...S.actionBar, padding: ehMobile ?'10px 12px' : S.actionBar.padding }}>
        {typeof onDeploy === 'function' && (
          <button
            style={{
              ...(isDeploying ?S.deployBtnDeploying : S.deployBtn),
              width: ehMobile ?'100%' : 'auto',
              justifyContent: 'center',
              minHeight: ehMobile ?44 : 'auto',
              opacity: botoesBloqueados ?0.65 : 1,
              cursor: botoesBloqueados ?'not-allowed' : (isDeploying ?S.deployBtnDeploying.cursor : S.deployBtn.cursor),
            }}
            onClick={handleDeploy}
            disabled={botoesBloqueados}
          >
            {isDeploying ?'Fazendo deploy...' : 'Deploy'}
          </button>
        )}

        {ehMobile ?(
          <div style={{ display: 'flex', gap: 8, width: '100%', flexWrap: 'wrap' }}>
            {typeof onEditProject === 'function' && (
              <button
                style={{
                  ...S.editBtn,
                  flex: 1,
                  minHeight: 44,
                  opacity: bloquearAcoes ?0.5 : 1,
                  cursor: bloquearAcoes ?'not-allowed' : S.editBtn.cursor,
                }}
                onClick={() => {
                  if (bloquearAcoes) return;
                  onEditProject(project);
                }}
                disabled={bloquearAcoes}
              >
                Editar
              </button>
            )}
            {typeof onOpenHistory === 'function' && (
              <button
                style={{
                  ...S.historyBtn,
                  flex: 1,
                  minHeight: 44,
                  opacity: bloquearAcoes ?0.5 : 1,
                  cursor: bloquearAcoes ?'not-allowed' : S.historyBtn.cursor,
                }}
                onClick={() => {
                  if (bloquearAcoes) return;
                  onOpenHistory(project);
                }}
                disabled={bloquearAcoes}
              >
                Histórico
              </button>
            )}
            {typeof onOpenCommits === 'function' && project.repoPath && (
              <button
                style={{
                  ...S.historyBtn,
                  flex: 1,
                  minHeight: 44,
                  opacity: bloquearAcoes ?0.5 : 1,
                  cursor: bloquearAcoes ?'not-allowed' : S.historyBtn.cursor,
                }}
                onClick={() => {
                  if (bloquearAcoes) return;
                  onOpenCommits(project);
                }}
                disabled={bloquearAcoes}
              >
                Commits
              </button>
            )}
            {typeof onAtualizarStatus === 'function' && (
              <button
                style={{
                  ...S.historyBtn,
                  flex: 1,
                  minHeight: 44,
                  background: '#f0fdf4',
                  color: '#15803d',
                  border: '1px solid #86efac',
                  opacity: (bloquearAcoes || atualizandoStatus) ?0.5 : 1,
                  cursor: (bloquearAcoes || atualizandoStatus) ?'not-allowed' : S.historyBtn.cursor,
                }}
                onClick={() => {
                  if (bloquearAcoes || atualizandoStatus) return;
                  onAtualizarStatus(project.id);
                }}
                disabled={bloquearAcoes || atualizandoStatus}
              >
                {atualizandoStatus ?'Atualizando...' : 'Atualizar Status'}
              </button>
            )}
          </div>
        ) : (
          <>
            {typeof onEditProject === 'function' && (
              <button
                style={{
                  ...S.editBtn,
                  opacity: bloquearAcoes ?0.5 : 1,
                  cursor: bloquearAcoes ?'not-allowed' : S.editBtn.cursor,
                }}
                onClick={() => {
                  if (bloquearAcoes) return;
                  onEditProject(project);
                }}
                disabled={bloquearAcoes}
              >
                Editar projeto
              </button>
            )}
            {typeof onOpenHistory === 'function' && (
              <button
                style={{
                  ...S.historyBtn,
                  opacity: bloquearAcoes ?0.5 : 1,
                  cursor: bloquearAcoes ?'not-allowed' : S.historyBtn.cursor,
                }}
                onClick={() => {
                  if (bloquearAcoes) return;
                  onOpenHistory(project);
                }}
                disabled={bloquearAcoes}
              >
                Histórico de utilizações
              </button>
            )}
            {typeof onOpenCommits === 'function' && project.repoPath && (
              <button
                style={{
                  ...S.historyBtn,
                  opacity: bloquearAcoes ?0.5 : 1,
                  cursor: bloquearAcoes ?'not-allowed' : S.historyBtn.cursor,
                }}
                onClick={() => {
                  if (bloquearAcoes) return;
                  onOpenCommits(project);
                }}
                disabled={bloquearAcoes}
              >
                Ver commits
              </button>
            )}
            {typeof onAtualizarStatus === 'function' && (
              <button
                style={{
                  ...S.historyBtn,
                  background: '#f0fdf4',
                  color: '#15803d',
                  border: '1px solid #86efac',
                  opacity: (bloquearAcoes || atualizandoStatus) ?0.5 : 1,
                  cursor: (bloquearAcoes || atualizandoStatus) ?'not-allowed' : S.historyBtn.cursor,
                }}
                onClick={() => {
                  if (bloquearAcoes || atualizandoStatus) return;
                  onAtualizarStatus(project.id);
                }}
                disabled={bloquearAcoes || atualizandoStatus}
              >
                {atualizandoStatus ?'Atualizando...' : 'Atualizar Status'}
              </button>
            )}
          </>
        )}

        <div
          style={{
            ...S.statusGroup,
            marginLeft: ehMobile ?0 : S.statusGroup.marginLeft,
            width: ehMobile ?'100%' : 'auto',
            justifyContent: ehMobile ?'flex-start' : S.statusGroup.justifyContent,
          }}
        >
          {project.lastDeploy && (
            <span style={S.lastDeploy}>Último: {new Date(project.lastDeploy.timestamp).toLocaleString('pt-BR')}</span>
          )}
          <span
            style={{
              ...S.statusBadge,
              color: statusInfo.color,
              background: statusInfo.bg,
              border: `1px solid ${statusInfo.border}`,
            }}
          >
            {statusInfo.label}
          </span>
        </div>
      </div>

      <div style={S.logArea}>
        <div style={{ ...S.logHeader, padding: ehMobile ?'9px 12px' : S.logHeader.padding }}>Logs do deploy</div>
        <div style={{ ...S.logBody, padding: ehMobile ?'10px 12px' : S.logBody.padding }}>
          {logs.length === 0 ?(
            <div style={S.logEmpty}>Nenhum log disponível. Clique em Deploy para iniciar.</div>
          ) : (
            logs.map((log, i) => (
              <div key={i} style={S.logLine}>
                <span style={S.logTimestamp}>{new Date(log.timestamp).toLocaleTimeString('pt-BR')}</span>
                <span style={{ ...S.logMessage, color: LOG_COLORS[log.level] || LOG_COLORS.info }}>
                  {log.message}
                </span>
              </div>
            ))
          )}
          <div ref={logEndRef} />
        </div>
      </div>
    </div>
  );
}

